import { newBlockId } from '@features/editorial/_shared/body';
import type { BlockNode, BlockTree } from '@shared/block-renderer';

import { isPageBlockType, type PageBlockType } from './blocks';

/**
 * The About body's wire boundary — **one module, both directions**, and the
 * single place the storage-shape conflict of `./types.ts` is resolved.
 *
 * The console holds a block tree, because that is what the pack draws and what
 * this repo's PRD-TDD decides. `content__informational_pages` stores
 * `body_html TEXT`, and `backend-content` §15.8 records the block tree as a
 * **deliberately rejected** alternative rather than as an oversight — so the
 * owning side wins on the wire.
 *
 * **This is a projection, not a substitution, and that is what makes it safe
 * here and not on the editorial body.** All three page kinds have exactly one
 * legal tag — `heading` → `<h2>`, `paragraph` → `<p>`, `list` → `<ul><li>` —
 * which are the three the public renderer emits, with no classes on any of them.
 * The editorial body cannot do this: two of its seven kinds render tags the
 * allow-list does not carry, so mapping them would change what a reader is told
 * a passage is.
 *
 * **Nothing here authors markup on the operator's behalf.** The operator types
 * text into a labelled control; this module escapes it and wraps it in the one
 * tag its kind declares. There is no raw-HTML input path anywhere on the
 * surface, which is the property the sanitiser on the other side exists to
 * enforce and this side must not undermine.
 *
 * **A body this build cannot express is never silently flattened.** `fromWire`
 * reports it instead, the surface renders the reason, and the editor does not
 * open — because a save from a canvas that dropped half the page would destroy
 * prose the operator never saw.
 */

const TAG_FOR: Readonly<Record<PageBlockType, 'h2' | 'p' | 'ul'>> = {
  heading: 'h2',
  paragraph: 'p',
  list: 'ul',
};

const KIND_FOR: Readonly<Record<string, PageBlockType>> = {
  H2: 'heading',
  P: 'paragraph',
  UL: 'list',
};

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function items(block: BlockNode): string[] {
  return Array.isArray(block.data.items)
    ? block.data.items.filter((item): item is string => typeof item === 'string')
    : [];
}

function text(block: BlockNode): string {
  return typeof block.data.text === 'string' ? block.data.text : '';
}

/** The tree, as the one HTML shape the public renderer draws. */
export function toBodyHtml(tree: BlockTree): string {
  return tree
    .flatMap((block) => {
      if (!isPageBlockType(block.type)) return [];

      if (block.type === 'list') {
        const rows = items(block)
          .filter((item) => item.trim().length > 0)
          .map((item) => `<li>${escapeHtml(item)}</li>`)
          .join('');
        // A list with no item is dropped rather than emitted empty: `<ul></ul>`
        // renders as a gap on the public page and reads as a layout defect.
        return rows.length > 0 ? [`<ul>${rows}</ul>`] : [];
      }

      const tag = TAG_FOR[block.type];
      return [`<${tag}>${escapeHtml(text(block))}</${tag}>`];
    })
    .join('\n');
}

export interface ParsedBody {
  blocks: BlockTree;
  /**
   * True when the stored body carries structure these three kinds cannot hold.
   *
   * The surface reads this rather than assuming: an editor opened over a body it
   * would silently truncate is the defect this flag exists to make visible.
   */
  unsupported: boolean;
}

/**
 * The stored HTML, back into blocks.
 *
 * `DOMParser` builds an **inert** document — no script runs, no resource loads —
 * and only `textContent` is read out of it. No value from this function ever
 * reaches `dangerouslySetInnerHTML`; there is no such call in this feature.
 */
export function fromBodyHtml(html: string | null | undefined): ParsedBody {
  const source = (html ?? '').trim();
  if (source.length === 0) return { blocks: [], unsupported: false };

  const document = new DOMParser().parseFromString(source, 'text/html');
  const blocks: BlockNode[] = [];
  let unsupported = false;

  for (const node of Array.from(document.body.childNodes)) {
    // Whitespace between two block tags is formatting, not content.
    if (node.nodeType === Node.TEXT_NODE) {
      if ((node.textContent ?? '').trim().length > 0) unsupported = true;
      continue;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      unsupported = true;
      continue;
    }

    const element = node as Element;
    const kind = KIND_FOR[element.tagName];

    if (!kind) {
      unsupported = true;
      continue;
    }

    if (kind === 'list') {
      const rows = Array.from(element.children);
      // A nested list, or anything inside the `<ul>` that is not an `<li>`, is
      // structure this editor would drop on the next save.
      if (rows.some((row) => row.tagName !== 'LI' || row.children.length > 0)) unsupported = true;
      blocks.push({
        id: newBlockId(),
        type: 'list',
        data: { items: rows.map((row) => (row.textContent ?? '').trim()).filter((row) => row.length > 0) },
      });
      continue;
    }

    // An inline element inside a heading or a paragraph — a link, an emphasis —
    // is the inline layer, which this editor does not hold. Reported, not eaten.
    if (element.children.length > 0) unsupported = true;

    blocks.push({ id: newBlockId(), type: kind, data: { text: element.textContent ?? '' } });
  }

  return { blocks, unsupported };
}
